import { NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbToastrService,
  NbTooltipModule
} from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { RouterState } from 'src/app/features/auth/redux';
import { DRB_ID_TYPE, ENVIRONMENTS } from 'src/app/shared/constants/general-constants';
import { NgxConfirmModalComponent } from 'src/app/shared/ui/ngx-confirm-modal/ngx-confirm-modal.component';
import { SpinnerState } from 'src/app/shared/ui/spinner/redux/spinner.state';
import { SharedModule } from '../../../shared/shared.module';
import { ViewCampaignComponent } from '../../campaigns/campaign-table/view-campaign/view-campaign.component';
import { CampaignState, LoadCampaigns } from '../../campaigns/redux';
import { ViewContentComponent } from '../../content/content-table/view-content/view-content.component';
import { ContentState, LoadContent } from '../../content/redux';
import {
  DrbState,
  DrbStateModel,
  SaveSourceFilesState,
  SaveSourceImagesState,
  SaveSourceLinksState,
  SaveSourceVideosState,
  SaveXliffState,
  loadDrbAssociatedItems
} from '../redux';
import { DrbService } from '../services/drb.service';

@Component({
  selector: 'app-delete-drb',
  templateUrl: './delete-drb.component.html',
  styleUrls: ['./delete-drb.component.css'],
  imports: [NbCardModule, NbIconModule, NbButtonModule, NgIf, SharedModule, FormsModule, NgFor, NbTooltipModule]
})
export class DeleteDrbComponent implements OnInit, OnDestroy {
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  public associatedItem$: Observable<any> = inject(Store).select(DrbState.associatedItems);
  public loading$: Observable<boolean> = inject(Store).select(SpinnerState.isLoading);
  private unsubscribe = new Subject<void>();

  public spin;
  public item;
  public type;
  public affectedCampaignsProdIDs = [];
  public affectedContentProdIDs = [];
  public sourceItemState: any[];
  public bundle_name: string;

  public affectedCampaignsStage = [];
  public affectedContentStage = [];
  public username = '';
  constructor(
    protected ref: NbDialogRef<DeleteDrbComponent>,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private service: DrbService,
    private store: Store
  ) {
    if (!this.store.selectSnapshot(CampaignState.campaign)) {
      this.store.dispatch(LoadCampaigns);
    }
    if (!this.store.selectSnapshot(ContentState.content)) {
      this.store.dispatch(LoadContent);
    }
  }

  public ngOnInit(): void {
    const drb = this.store.selectSnapshot(DrbState.getByKey(this.bundle_name as keyof DrbStateModel));
    switch (this.type) {
      case DRB_ID_TYPE.STRING:
        this.sourceItemState = drb.xliff.en;
        break;
      case DRB_ID_TYPE.FILE:
        this.sourceItemState = drb.source.files;
        break;
      case DRB_ID_TYPE.LINK:
        this.sourceItemState = drb.source.links;
        break;
      case DRB_ID_TYPE.VIDEO:
        this.sourceItemState = drb.source.videos;
        break;
      case DRB_ID_TYPE.IMAGE:
        this.sourceItemState = drb.source.images;
        break;
    }
    this.store.dispatch(new loadDrbAssociatedItems(this.item.id));
    this.associatedItem$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        [this.affectedCampaignsStage, this.affectedContentStage] = this.service.setupAffectedItems(
          data,
          ENVIRONMENTS.STAGE
        );
        [this.affectedCampaignsProdIDs, this.affectedContentProdIDs] = this.service.setupAffectedItems(
          data,
          ENVIRONMENTS.PROD
        );
      }
    });
    this.loading$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.spin = data));
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public clear(): void {
    this.affectedCampaignsProdIDs = [];
    this.affectedContentProdIDs = [];
    this.sourceItemState = [];
    this.affectedCampaignsStage = [];
    this.affectedContentStage = [];
    this.username = '';
  }

  public close(): void {
    this.clear();
    this.ref.close();
  }

  public deletionEnabled() {
    if (
      this.affectedCampaignsProdIDs.length > 0 ||
      this.affectedContentProdIDs.length > 0 ||
      this.affectedCampaignsStage.length > 0 ||
      this.affectedContentStage.length > 0
    ) {
      return true;
    }

    return false;
  }

  public delete(): void {
    const localSourceItem = JSON.parse(JSON.stringify(this.sourceItemState));
    const index = localSourceItem.findIndex((i) => i.id === this.item.id);
    if (index > -1) {
      delete localSourceItem[index];
      localSourceItem.splice(index, 1);
    }

    switch (this.type) {
      case DRB_ID_TYPE.STRING:
        this.store.dispatch(new SaveXliffState(localSourceItem, this.bundle_name));
        break;
      case DRB_ID_TYPE.FILE:
        this.store.dispatch(new SaveSourceFilesState(localSourceItem, this.bundle_name));
        break;
      case DRB_ID_TYPE.IMAGE:
        this.store.dispatch(new SaveSourceImagesState(localSourceItem, this.bundle_name));
        break;
      case DRB_ID_TYPE.LINK:
        this.store.dispatch(new SaveSourceLinksState(localSourceItem, this.bundle_name));
        break;
      case DRB_ID_TYPE.VIDEO:
        this.store.dispatch(new SaveSourceVideosState(localSourceItem, this.bundle_name));
        break;
    }

    this.close();
  }

  public confirmDeletion() {
    this.dialogService
      .open(NgxConfirmModalComponent, {
        context: {
          title: $localize`Confirm DELETE`,
          yesButton: $localize`DELETE`,
          noButton: $localize`Cancel`,
          message:
            $localize`Take notice that deleting the DRB Item is an irreversible operation.` +
            '\n\n' +
            $localize`To save, type CONFIRM below and click DELETE`
        }
      })
      .onClose.subscribe((data) => {
        if (data) {
          this.delete();
        }
      });
  }

  public viewCampaign(event): void {
    const found = this.affectedCampaignsStage.find((element) => element.id === event);
    if (found) {
      this.dialogService.open(ViewCampaignComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          campaign: found
        }
      });
    } else {
      this.toastrService.show(status, $localize`Campaign with this ID does not exist`, {
        status: 'danger'
      });
    }
  }

  public viewContent(event): void {
    const found = this.affectedContentStage.find((element) => element.id === event);
    if (found) {
      this.dialogService.open(ViewContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          content: found
        }
      });
    } else {
      this.toastrService.show(status, $localize`Content with this Content ID does not exist`, {
        status: 'danger'
      });
    }
  }
}
